// The Money page's write calls, kept out of the component so they can be
// tested without rendering anything — the same split Tasks.tsx uses for
// setStatus/saveNote.
//
// Every one of these posts to an endpoint that already enforces the rules:
// the clinic must be one the caller may write to (INV-3, decided from the
// session and never from this body), the amount must be a non-negative
// number, and the Idempotency-Key means a double-click replays instead of
// logging a second deposit. Nothing here re-implements those checks — a
// client-side copy of a server rule is a copy that drifts. What this module
// does is send the right shape and fold the result into the store.
import { authedWrite, ApiError } from './apiClient';
import { upsertEntity, getEntityTable, putEntityTable } from './store';

export const DEPOSIT_METHODS = ['CASH', 'CHECK', 'CARD', 'MIXED'] as const;
export const PETTY_CASH_DIRECTIONS = ['IN', 'OUT'] as const;

/** The tables this page can write and soft delete, with the URL slug each one
 * uses. The first three are the live app's own three (page_money.gs's
 * MONEY_TABS_); otherCashItem is new (owner, 2026-08-15) — cash that is none
 * of the other three. */
export const MONEY_SLUG = {
  deposit: 'deposits',
  drawerCount: 'drawer-counts',
  pettyCash: 'petty-cash',
  otherCashItem: 'other-cash',
  depositBatch: 'deposit-batches',
} as const;

export type MoneyEntity = keyof typeof MONEY_SLUG;

export interface DepositInput {
  locationCode: string;
  depositDate: string;
  amount: string;
  method: string;
}

export interface DrawerCountInput {
  locationCode: string;
  countDate: string;
  countedAmount: string;
  expectedAmount: string;
}

export interface PettyCashInput {
  locationCode: string;
  transactionDate: string;
  direction: string;
  amount: string;
  reason: string;
}

export interface OtherCashItemInput {
  locationCode: string;
  itemDate: string;
  direction: string;
  amount: string;
  description: string;
}

/**
 * One argument used to do two jobs here, and it broke every Money add in the
 * app after the first one ever made.
 *
 * `created(entity, path, body, key)` passed `key` BOTH as the name of the
 * property to read off the response envelope AND as the Idempotency-Key header.
 * The callers pass literals — `'pettyCash'`, `'deposit'` — so every petty-cash
 * entry any user ever submitted was sent with the header
 * `Idempotency-Key: pettyCash`. The first such write in the database's lifetime
 * succeeded; every later one carries the same key with a different body, which
 * the server correctly refuses:
 *
 *     Idempotency-Key reused with a different request body
 *
 * It looked like it worked because it DID work — once, on 2026-08-16, which is
 * the day the smoke check first exercised it. It could never work again. Found
 * in the browser on 2026-08-16, on the deploy before managers were to start
 * using this page.
 *
 * The envelope name and the idempotency key are now separate things, because
 * they always were: `responseKey` is about the shape of the reply, the header is
 * about this one attempt. The header is minted per ATTEMPT by `keyedWrite`
 * below (Cursor review #30): fresh for each new entry, the same on a retry.
 *
 * WHAT THIS DOES AND DOES NOT PROTECT, stated plainly rather than implied: a
 * fresh key per call means the server can no longer collapse a genuine
 * double-submit of the same row. The guard against that is `useWrite`'s `busy`
 * flag, which ignores a second click while the first is in flight — a UI guard,
 * not a server one. Keying off a hash of the body was the alternative and was
 * rejected: it would silently swallow a manager's SECOND legitimate $5 petty
 * cash entry of the day, which is a worse failure than a rare duplicate row
 * somebody can delete.
 */
/**
 * ONE KEY PER ATTEMPT, KEPT ACROSS A RETRY (Cursor review #30). A fresh key
 * per call meant a save that landed on the server, then lost its reply (a
 * dropped connection, a timeout), was stored AGAIN when the person pressed
 * Save a second time — two deposits.
 *
 * So the key now belongs to the attempt, not the call: the same write (same
 * method, path and body) sent again after a failure that might have landed —
 * no reply at all, or a 5xx — carries the SAME key, and the server replays
 * the first result instead of storing a second row. A success or a definite
 * refusal (4xx) ends the attempt, so the next identical entry is a new one:
 * the manager's second legitimate $5 petty-cash line of the day still saves.
 */
const pendingKeys = new Map<string, string>();

async function keyedWrite<T>(
  method: 'POST' | 'PATCH',
  path: string,
  body: unknown,
): Promise<T> {
  const attempt = `${method} ${path} ${JSON.stringify(body)}`;
  const key = pendingKeys.get(attempt) ?? crypto.randomUUID();
  pendingKeys.set(attempt, key);
  try {
    const res = await authedWrite<T>(method, path, body, key);
    pendingKeys.delete(attempt);
    return res;
  } catch (err) {
    const status = err instanceof ApiError ? err.status : 0;
    if (status >= 400 && status < 500) pendingKeys.delete(attempt);
    throw err;
  }
}

async function created<T extends { id: number }>(
  entity: MoneyEntity,
  path: string,
  body: unknown,
  responseKey: string,
): Promise<T> {
  const res = await keyedWrite<Record<string, T>>('POST', path, body);
  const row = res[responseKey];
  await upsertEntity(entity, row.id, row);
  return row;
}

export function addDeposit(input: DepositInput): Promise<{ id: number }> {
  return created('deposit', '/api/v1/money/deposits', input, 'deposit');
}

export function addDrawerCount(input: DrawerCountInput): Promise<{ id: number }> {
  return created('drawerCount', '/api/v1/money/drawer-counts', input, 'drawerCount');
}

export function addPettyCash(input: PettyCashInput): Promise<{ id: number }> {
  return created('pettyCash', '/api/v1/money/petty-cash', input, 'pettyCash');
}

export function addOtherCashItem(input: OtherCashItemInput): Promise<{ id: number }> {
  return created('otherCashItem', '/api/v1/money/other-cash', input, 'otherCashItem');
}

/** Same shape as `created` above, but PATCHing one existing row by id (owner
 * rule 2026-09-10: every table has Add and Edit). Keyed per attempt by
 * `keyedWrite`, exactly as `created` is. */
async function updated<T extends { id: number }>(
  entity: MoneyEntity,
  path: string,
  body: unknown,
  responseKey: string,
): Promise<T> {
  const res = await keyedWrite<Record<string, T>>('PATCH', path, body);
  const row = res[responseKey];
  await upsertEntity(entity, row.id, row);
  return row;
}

export async function updatePettyCash(id: number, input: PettyCashInput): Promise<{ id: number }> {
  // The PATCH reply carries the entry without its receipts (they are a separate
  // table); keep the ones already in the store rather than dropping them.
  const before = getEntityTable('pettyCash')[String(id)] as { receipts?: unknown[] } | undefined;
  const row = await updated<{ id: number }>('pettyCash', `/api/v1/money/petty-cash/${id}`, input, 'pettyCash');
  if (before?.receipts?.length) {
    await upsertEntity('pettyCash', id, { ...row, receipts: before.receipts });
  }
  return row;
}

export function updateOtherCashItem(id: number, input: OtherCashItemInput): Promise<{ id: number }> {
  return updated('otherCashItem', `/api/v1/money/other-cash/${id}`, input, 'otherCashItem');
}

export interface DepositReconInput {
  locationCode: string;
  reconDate: string;
  mr080?: string | null;
  envelope?: string | null;
  overUnder?: string | null;
  batchAmount?: string | null;
  mr080Batch?: string | null;
  batchOverUnder?: string | null;
  notes?: string | null;
}

export async function saveDepositRecon(input: DepositReconInput): Promise<{ id: number }> {
  const res = await keyedWrite<{ depositRecon: { id: number } }>(
    'PATCH',
    '/api/v1/money/deposit-recons',
    input,
  );
  const row = res.depositRecon;
  await upsertEntity('depositRecon', row.id, row);
  return row;
}

export interface DepositBatchInput {
  id?: number;
  locationCode: string;
  dateStart: string;
  dateEnd: string;
  mr080Amount?: string | null;
  batchAmount?: string | null;
  depositAmount?: string | null;
  notes?: string | null;
  closed?: boolean;
}

export async function saveDepositBatch(input: DepositBatchInput): Promise<{ id: number }> {
  const res = await keyedWrite<{ depositBatch: { id: number } }>(
    'PATCH',
    '/api/v1/money/deposit-batches',
    input,
  );
  const row = res.depositBatch;
  await upsertEntity('depositBatch', row.id, row);
  return row;
}

/**
 * Soft delete: the server flips a `deleted` flag and never removes the row
 * (migration 020, page_money.gs's own convention). The row is dropped from
 * the local store on success because every read path filters deleted rows
 * out — leaving it on screen would show something the next reload will not.
 */
export async function softDeleteMoneyRow(entity: MoneyEntity, id: number): Promise<void> {
  await authedWrite('DELETE', `/api/v1/money/${MONEY_SLUG[entity]}/${id}`);
  const table = { ...getEntityTable(entity) };
  delete table[String(id)];
  await putEntityTable(entity, table);
}
